import { ChangeDetectionStrategy, Component, computed, inject, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router, RouterLink } from '@angular/router';
import { CanViewPageDirective } from '../../../auth/directives/can-view-page.directive';
import { HideForOnboardingDirective } from '../../../auth/directives/hide-for-onboarding.directive';
import { UserStore } from 'app/core/services/user.store';
import { ModulesStore } from '../../../../features/admin/modules/modules.store';
import { BILLING_PAGE_CODE } from '../../../../features/admin/modules/modules-pages/templates/billing/billing.types';
import { LayoutService } from 'app/shared/services/layout.service';
import { TippyTooltipDirective } from 'app/shared/directives/tippy-tooltip.directive';
import { CompanyIdentityComponent } from '../company-identity/company-identity.component';
import { CompanyIdentityService } from '../company-identity/company-identity.service';

/**
 * The user menu in the top bar. Its trigger is the company identity block (Sentry
 * DISTRODOT-PORTAL-MR): the viewer's company as logo-or-initials and name, the signed-in user as
 * the second line, a chevron because it opens a menu. One button, one tabstop; the aria-label
 * names company and user ("Gebruikersmenu: Kievit Transport B.V., Edwin Trodis"), the tooltip
 * carries the full top line for a truncated name. On a small screen only the avatar shows.
 *
 * Impersonation lives here too (the separate switcher button left the icon row): while an admin
 * views as someone else the trigger gets a 2px accent border, a back badge on the avatar, the
 * second line "Je werkt als <naam> · <rol>" in the accent colour — no warning tokens, the banner
 * carries that — and the menu opens with "Je werkt als <naam>" plus a compact icon item for the way
 * back to the OWN account (named from `originalUser`, never from `currentUser`, which is the viewed
 * user then) and the OTHER recent takeovers; "Ingelogd als" is left out while impersonating, it would repeat the
 * viewed name. An admin who is not impersonating finds "Recente overnames" right under "Ingelogd als".
 * The full-width banner stays as the safety signal, with the same words. The rest of the menu (Profiel, Bedrijfsinstellingen, Facturatie,
 * Instellingen, Uitloggen) is unchanged.
 *
 * `data-cy="user-menu"` stays on the trigger (specs and the selector catalogue), and the trigger
 * is a plain button, not a mat-icon-button: the 36px icon-button rule for this toolbar
 * (_buttons.scss) and the modern layout's width pass (`shrinkNavigationOnOverflow`) both skip it.
 * Its styling (`.dd-user-menu-trigger`) lives in _buttons.scss next to the other top-bar button
 * rules, so Storybook renders the same trigger around `dd-company-identity` without this component.
 */
@Component({
  selector: 'user',
  template: `
    <!-- Button -->
    @if (userStore.currentUser()) {
      <button
        type="button"
        class="dd-user-menu-trigger"
        [class.dd-user-menu-trigger--compact]="compact()"
        [class.dd-user-menu-trigger--impersonating]="identity.impersonating()"
        [matMenuTriggerFor]="userActions"
        [attr.aria-label]="triggerLabel()"
        [tippyTooltip]="tooltip()"
        [tippyTooltipAria]="false"
        data-cy="user-menu">
        <dd-company-identity
          [name]="identity.title()"
          [logoLightSrc]="identity.logoLightSrc()"
          [logoDarkSrc]="identity.logoDarkSrc()"
          [subline]="identity.subline()"
          [chevron]="!compact()"
          [impersonating]="identity.impersonating()"
          [initialsColorLight]="identity.initialsColorLight()"
          [initialsColorDark]="identity.initialsColorDark()"
          [compact]="compact()" />
      </button>

      <mat-menu [xPosition]="'before'" #userActions="matMenu">
        @if (userStore.impersonationData()) {
          <!-- Viewing as someone else: who, the way back first, then the OTHER recent takeovers. One
               vocabulary with the banner and the trigger: "Je werkt als". -->
          <!-- One row: who you work as, and at the right the compact way back — a mat-menu-item (so
               the arrow keys and the first focus reach it) styled as a 36px icon button; its
               accessible name and tooltip carry the full sentence and the own account's name. -->
          <div
            class="dd-menu-heading dd-menu-impersonation-row py-2"
            data-cy="impersonation-section"
            id="dd-user-menu-impersonation-heading">
            <span class="text-sm flex flex-col leading-none min-w-0">
              <span>Je werkt als</span>
              <span class="mt-1.5 font-bold truncate" data-cy="impersonated-user">{{ identity.userName() }}</span>
              @if (identity.userRole(); as role) {
                <span class="mt-1 dd-menu-subline truncate" data-cy="impersonated-role">{{ role }}</span>
              }
            </span>
            <button
              mat-menu-item
              class="dd-menu-icon-item"
              (click)="userStore.stopImpersonating()"
              [attr.aria-label]="stopImpersonatingLabel()"
              [tippyTooltip]="stopImpersonatingLabel()"
              [tippyTooltipAria]="false"
              data-cy="switcher-stop-impersonating">
              <mat-icon [svgIcon]="'heroicons_solid:arrow-uturn-left'"></mat-icon>
            </button>
          </div>
          <!-- The recent takeovers follow below, under this heading; no closing divider here: every
               group below (Profiel…, Uitloggen) brings its own leading one. -->
        } @else {
          <!-- Own account. Not while impersonating: currentUser() IS the viewed user then, so this
               block would repeat the "Je werkt als" name; the own name sits on the way back. -->
          <div class="dd-menu-heading py-2" data-cy="logged-in-section">
            <span class="text-sm flex flex-col leading-none">
              <span>Ingelogd als</span>
              <span class="mt-1.5 font-bold" data-cy="logged-in-user">{{ identity.userName() }}</span>
              @if (userStore.emailAddress(); as email) {
                <span class="mt-1 dd-menu-subline truncate">{{ email }}</span>
              }
            </span>
          </div>
        }

        @if (showsRecentUsers()) {
          <!-- The recent takeovers, ONE block for both states: while impersonating the OTHER takeovers
               sit right under "Je werkt als"; on the own account an admin finds them right under
               "Ingelogd als" (both say who you are; product ruling 2026-10-22), behind a divider.
               Inline, not through an ng-template outlet: items rendered from a template declared
               outside <mat-menu> fall out of its item query and never reach the FocusKeyManager,
               so the arrow keys would skip the whole list (review 2026-10-21). The group wrapper
               (display: contents) gives the rows their heading for assistive tech; MatMenu's
               descendant query still finds the items. -->
          @if (canSwitchUsers()) {
            <mat-divider></mat-divider>
          }
          <div role="group" aria-labelledby="dd-user-menu-recent-heading" class="contents" data-cy="recent-impersonations-group">
            <div class="dd-menu-heading pt-3 pb-1 text-sm font-medium" data-cy="recent-impersonations-heading" id="dd-user-menu-recent-heading">Recente overnames</div>
            @for (recent of visibleRecentUsers(); track recent.userId; let index = $index) {
              <button
                mat-menu-item
                (click)="userStore.switchImpersonation(recent.userId)"
                [attr.data-cy]="'recent-impersonation-' + index">
                <div class="flex flex-col leading-tight py-1">
                  <span class="truncate">{{ recent.label }}</span>
                  @if (recent.subLabel) {
                    <span class="dd-menu-subline truncate">{{ recent.subLabel }}</span>
                  }
                </div>
              </button>
            } @empty {
              <div class="dd-menu-heading py-2 dd-menu-subline" data-cy="no-recent-impersonations">{{ noRecentUsersText() }}</div>
            }
            @if (visibleRecentUsers().length) {
              <button mat-menu-item (click)="userStore.clearRecentImpersonations()" data-cy="clear-recent-impersonations">
                <span class="dd-menu-subline">Recente overnames wissen</span>
              </button>
            }
          </div>
        }

        <ng-container *hideForOnboarding>
          <ng-container *canViewPage="{ pageId: profilePageId, show: true }">
            <mat-divider></mat-divider>
            <a mat-menu-item routerLink="/profiel" data-cy="user-menu_profile-button">
              <mat-icon [svgIcon]="'feather:user'"></mat-icon>
              <span>Profiel</span>
            </a>
          </ng-container>
          <ng-container *canViewPage="{ pageId: companyPageId, show: true }">
            <mat-divider></mat-divider>
            <button mat-menu-item routerLink="/bedrijfsinstellingen" data-cy="user-menu_company-button">
              <mat-icon [svgIcon]="'mat_outline:business'"></mat-icon>
              <span>Bedrijfsinstellingen</span>
            </button>
          </ng-container>
          <ng-container *canViewPage="{ pageId: billingPageId, show: true }">
            <mat-divider></mat-divider>
            <a mat-menu-item routerLink="/facturatie" data-cy="user-menu_billing-button">
              <mat-icon [svgIcon]="'mat_outline:receipt_long'"></mat-icon>
              <span>Facturatie</span>
            </a>
          </ng-container>
          <!-- Instellingen also lives behind the onboarding gate: AuthGuard sends
               every slug except /onboarding back, so it would be a dead link. -->
          <ng-container *canViewPage="{ pageId: usersettingsPageId, show: true }">
            <mat-divider></mat-divider>
            <a mat-menu-item routerLink="/gebruikersinstellingen" data-cy="user-menu_usersettings-button">
              <mat-icon [svgIcon]="'feather:settings'"></mat-icon>
              <span>Instellingen</span>
            </a>
          </ng-container>
        </ng-container>
        <mat-divider></mat-divider>
        <button mat-menu-item (click)="signOut()" data-cy="user-menu_logout-button">
          <mat-icon [svgIcon]="'feather:log-out'"></mat-icon>
          <span>Uitloggen</span>
        </button>
      </mat-menu>

    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  exportAs: 'user',
  imports: [
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    MatDividerModule,
    RouterLink,
    CanViewPageDirective,
    HideForOnboardingDirective,
    TippyTooltipDirective,
    CompanyIdentityComponent,
  ],
})
export class UserComponent {
  readonly userStore = inject(UserStore);
  readonly modulesStore = inject(ModulesStore);
  readonly router = inject(Router);
  readonly identity = inject(CompanyIdentityService);
  readonly #layoutService = inject(LayoutService);

  readonly profilePageId = this.modulesStore.moduleCodeToId('profile');
  readonly companyPageId = this.modulesStore.moduleCodeToId('company-settings');
  readonly usersettingsPageId = this.modulesStore.moduleCodeToId('usersettings');
  readonly billingPageId = this.modulesStore.moduleCodeToId(BILLING_PAGE_CODE);

  /**
   * Narrow screen (below 960px): the bar has no room for two lines, the avatar alone is the
   * trigger. Not `isScreenSmall` (below 1280px, where the navigation collapses): at 200% zoom on
   * a laptop or on a tablet the bar still has room for the company name.
   */
  readonly compact = computed(() => this.#layoutService.isScreenNarrow());

  /** Both lines, so a truncated second line is readable somewhere (tippy turns \n into a line break). */
  readonly tooltip = computed(() => {
    const subline = this.identity.subline();
    return subline ? `${this.identity.title()}\n${subline}` : this.identity.title();
  });

  /**
   * "Gebruikersmenu: <bovenregel>, <onderregel>" — the full names, whatever the block truncates, and
   * exactly the visible words (WCAG 2.5.3 Label in Name), also while impersonating ("…, Je werkt
   * als <naam> · <rol>"). Plain "Gebruikersmenu"
   * while nothing is known about the viewer yet (no company, no name, no e-mail).
   */
  readonly triggerLabel = computed(() => {
    const title = this.identity.title();
    const subline = this.identity.subline();
    return title ? `Gebruikersmenu: ${title}${subline ? `, ${subline}` : ''}` : 'Gebruikersmenu';
  });

  /**
   * Who may jump into another user from here: an admin on their own account (the same gate the
   * former switcher button had, `isOriginalAdmin`). While impersonating the list sits in the
   * "Je werkt als" section instead.
   */
  readonly canSwitchUsers = computed(() => this.userStore.isOriginalAdmin() && !this.userStore.impersonationData());

  /**
   * The last ten impersonated users (moved here from the former `app-impersonation-switcher`).
   * Users are not guaranteed to have a name, so fall back to the e-mail address and only show a
   * second line when it adds information.
   */
  readonly recentUsers = computed(() =>
    this.userStore.recentImpersonations().map(recent => {
      const name = recent.name?.trim();
      const emailAddress = recent.emailAddress?.trim();
      return {
        userId: recent.userId,
        label: name || emailAddress || 'Onbekende gebruiker',
        subLabel: name ? emailAddress : null,
      };
    })
  );

  /** While impersonating: the takeovers other than the one on screen (that one has its own heading). */
  readonly otherRecentUsers = computed(() =>
    this.recentUsers().filter(recent => recent.userId !== this.userStore.selectedUserId())
  );

  /**
   * Whether the "Recente overnames" block renders at all: while impersonating (the other
   * takeovers, under "Je werkt als"), or for an admin on their own account (`canSwitchUsers`).
   */
  readonly showsRecentUsers = computed(() => !!this.userStore.impersonationData() || this.canSwitchUsers());

  /** The rows of that block: the OTHER takeovers while impersonating, else all of them. */
  readonly visibleRecentUsers = computed(() =>
    this.userStore.impersonationData() ? this.otherRecentUsers() : this.recentUsers()
  );

  /** The block's empty line: "no OTHER takeover" while impersonating, "none yet" on the own account. */
  readonly noRecentUsersText = computed(() =>
    this.userStore.impersonationData() ? 'Geen andere recente overnames' : 'Nog geen recente overnames'
  );

  /** The real account behind the impersonation, so "back" names where you land. */
  readonly originalUserLabel = computed(() => {
    const originalUser = this.userStore.originalUser();
    return originalUser?.name?.trim() || originalUser?.emailAddress?.trim() || '';
  });

  /** Accessible name and tooltip of the compact way-back item: the sentence plus the own account. */
  readonly stopImpersonatingLabel = computed(() => {
    const own = this.originalUserLabel();
    return `Terug naar eigen account${own ? ` (${own})` : ''}`;
  });

  viewUserAccount(id: string) {
    this.router.navigate(['/personeel/' + id]);
  }

  /**
   * Sign out
   */
  signOut(): void {
    this.router.navigate(['/sign-out']);
  }
}
